import * as THREE from 'three';
import { TextGeometry } from "./three-extensions/jsm/geometries/TextGeometry";
import Engine from './Engine';
import { CustomMaterials } from './CustomMaterials';
import MaterialAsset from './MaterialAsset';
import Text3D from './Text3D';
import ProjectSaver from './ProjectSaver';
import { assert } from './utilities/ErrorHandling';
import PartsCatalogUtils from './utilities/PartsCatalogUtils';
import DefaultSceneManager from './DefaultSceneManager';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import { RenderModes, CustomEventTypes, MaterialTypes, SaveModificationTypes } from '../globals';
import { VisibilityModes } from "../interfaces/enums";

interface IObjectOverrideParentParams
{
	object: THREE.Object3D;
	override: boolean;
}

export default class VisibilityManager
{
	static renderMode = RenderModes.Standalone;
	static manuallyHiddenObjects: THREE.Object3D[] = [];

	static isolateObjects(objects: THREE.Object3D[]) 
	{
		if(objects.length)
		{
			let requiresUpdate = false;
			const objectsToHide: THREE.Object3D[] = [];
			const root = Engine.getInstance().getSelectionRoot();
			root.traverse(function (child)
			{
				if(!objects.includes(child))
				{
					objectsToHide.push(child);
				}
				else if(VisibilityManager.manuallyHiddenObjects.includes(child))
				{
					VisibilityManager.manuallyHiddenObjects.splice(VisibilityManager.manuallyHiddenObjects.indexOf(child), 1);
					requiresUpdate = true;
				}
			});

			VisibilityManager.hideObjects(objectsToHide, false);
			if(requiresUpdate) VisibilityManager.setObjectsVisibilityMode([root], [root.userData.visibility[VisibilityManager.getRenderMode()]]);
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectManualHiddenStateChanged));
	}

	static hideObjects(objects: THREE.Object3D[], recursive = true)
	{
		function hide(object: THREE.Object3D)
		{
			if(!VisibilityManager.manuallyHiddenObjects.includes(object))
			{
				if(object instanceof Text3D)
				{
					for(const child of object.children)
					{
						if((child as THREE.Mesh).geometry && (child as THREE.Mesh).geometry instanceof TextGeometry)
						{
							VisibilityManager.manuallyHiddenObjects.push(object);
							VisibilityManager.manuallyHiddenObjects.push(child);
							(child as THREE.Mesh).material = CustomMaterials.getHiddenMaterial();
							break;
						}
					}
				}
				else if(object.userData.visibility && !object.userData.hideInHierarchy)
				{
					VisibilityManager.manuallyHiddenObjects.push(object);
					const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
					if(materialAsset) (object as THREE.Mesh).material = CustomMaterials.getHiddenMaterial();
				}
			}
		}

		for(const object of objects)
		{
			if(recursive)
			{
				object.traverse((child) => { hide(child); });
			}
			else
			{
				hide(object);
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectManualHiddenStateChanged));
	}

	static restoreObjects(objects?: THREE.Object3D[])
	{
		if(objects?.length)
		{
			for(const object of objects)
			{
				if(VisibilityManager.manuallyHiddenObjects.includes(object))
				{
					VisibilityManager.manuallyHiddenObjects.splice(VisibilityManager.manuallyHiddenObjects.indexOf(object), 1);
				}
			}
		}
		else
		{
			VisibilityManager.manuallyHiddenObjects.length = 0;
		}

		const root = Engine.getInstance().getSelectionRoot();
		VisibilityManager.setObjectsVisibilityMode([root], [root.userData.visibility[VisibilityManager.getRenderMode()]]);
		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.objectManualHiddenStateChanged));
	}

	static getHiddenObjects() : THREE.Object3D[]
	{
		return VisibilityManager.manuallyHiddenObjects;
	}

	static isObjectHidden(object: THREE.Object3D) : boolean 
	{
		return VisibilityManager.manuallyHiddenObjects.includes(object);
	}

	static getRenderMode() : RenderModes
	{
		return VisibilityManager.renderMode;
	}

	static setRenderMode(mode: RenderModes)
	{
		VisibilityManager.renderMode = mode;
		const currentSceneRoot = Engine.getInstance().getSelectionRoot();
		VisibilityManager.setObjectsVisibilityMode([currentSceneRoot], [currentSceneRoot.userData.visibility[mode]]);
		document.dispatchEvent(new CustomEvent(CustomEventTypes.renderModeChanged));
	}

	static setObjectOverrideParent(params: IObjectOverrideParentParams)
	{
		DefaultSceneManager.getInstance().syncVisibilityParentOverride(params.object, params.override);

		params.object.userData.visibility.overrideParent = params.override;
		VisibilityManager.setObjectsVisibilityMode([params.object], [params.object.userData.visibility[VisibilityManager.getRenderMode()]]);
		ProjectSaver.getInstance().markModifiedObject(params.object, SaveModificationTypes.Visibility);
	}

	static setObjectsVisibilityMode(objects: THREE.Object3D[], modes: VisibilityModes[], renderMode = VisibilityManager.getRenderMode(), customMaterial? : string)
	{
		if(PartsCatalogUtils.isPartsCatalogOpen()) return;
		
		for (let i = 0; i < objects.length; i++)
		{
			const object = objects[i];
			const visibilityMode = modes[i];
			const forceUpdate = (object.parent?.type === 'Scene'); //when this is called with selectionRoot, it implies wanting to fully update the scene

			if(!customMaterial)
			{
				if(renderMode === RenderModes.Standalone)
				{
					customMaterial = object.userData.visibility.customStandaloneMaterial;
				}
				else
				{
					customMaterial = object.userData.visibility.customDeviceMaterial;
				}
			}

			DefaultSceneManager.getInstance().syncVisibilityMode(object, visibilityMode, renderMode, customMaterial);
			object.userData.visibility[renderMode] = visibilityMode;
			if(renderMode === RenderModes.Standalone)
			{
				object.userData.visibility.customStandaloneMaterial = (customMaterial === MaterialAsset.defaultMaterialGuid) ? undefined : customMaterial;
			}
			else
			{
				object.userData.visibility.customDeviceMaterial = (customMaterial === MaterialAsset.defaultMaterialGuid) ? undefined : customMaterial;
			}
			
			if (renderMode !== VisibilityManager.renderMode) continue;

			if (object.userData.visibility.overrideParent)
			{
				setVisibilityModeRecursive(object, visibilityMode, customMaterial, forceUpdate);
			}
			else
			{
				//hidden overrides ghost which overrides solid
				//we need to find if any parent overrides this object's mode
				let overrideMode = visibilityMode;
				if (overrideMode !== VisibilityModes.Hidden)
				{
					let parent = object.parent;
					while (parent)
					{
						const parentVisibilityMode = (parent.userData) ? (parent.userData.visibility) ? parent.userData.visibility[renderMode] : undefined : undefined;
						if (parentVisibilityMode === undefined)
						{
							break;
						}
						else if (parent.userData.visibility.overrideParent)
						{
							overrideMode = parentVisibilityMode;
							break;
						}
						else if (parentVisibilityMode === VisibilityModes.Hidden)
						{
							overrideMode = VisibilityModes.Hidden;
							break;
						}
						else if (parentVisibilityMode === VisibilityModes.Ghosted)
						{
							overrideMode = VisibilityModes.Ghosted;
						}
						else if (parentVisibilityMode === VisibilityModes.Custom)
						{
							overrideMode = VisibilityModes.Custom;
							if(!customMaterial)
							{
								customMaterial =  renderMode === RenderModes.Standalone ? parent.userData.visibility.customStandaloneMaterial : parent.userData.visibility.customDeviceMaterial;
							}
						}
						parent = parent.parent;
					}
				}

				setVisibilityModeRecursive(object, overrideMode, customMaterial, forceUpdate);
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.visibilityModeChanged));
	}

	static getObjectVisualVisibilityMode(object: THREE.Object3D) : VisibilityModes
	{
		if(VisibilityManager.isObjectHidden(object)) return VisibilityModes.Hidden;
		let mode = object.userData.visibility[VisibilityManager.getRenderMode()];
		if (mode === VisibilityModes.Hidden) return VisibilityModes.Hidden;
		if (object.userData.visibility.overrideParent) return mode;

		let parent = object.parent;
		while(parent && parent.userData.visibility && parent.parent?.type !== 'Scene')
		{
			if(parent.userData.visibility.overrideParent)
			{
				return parent.userData.visibility[VisibilityManager.getRenderMode()];
			}
			if(parent.userData.visibility[VisibilityManager.getRenderMode()] === VisibilityModes.Hidden)
			{
				return VisibilityModes.Hidden;
			}
			if(parent.userData.visibility[VisibilityManager.getRenderMode()] === VisibilityModes.Ghosted)
			{
				mode = VisibilityModes.Ghosted;
			}

			parent = parent.parent;
		}

		return mode;
	}

}

function setVisibilityModeRecursive(object: THREE.Object3D, visibilityMode: VisibilityModes, customMaterial = "", forceUpdate = false)
{
	//if visibilityMode is hidden - set to hidden and call all children with hidden
	//if visibilityMode is ghosted - set to ghosted unless userData.visibilityMode is hidden (then do nothing). set all children with ghosted or hidden
	//if visibilityMode is solid - set based on userData.visibilityMode. set all children with userData.visibilityMode
	//if overrideParent is true - parent visibilityMode is ignored and current visibility is the new override
	if (object.userData.hideInHierarchy) return;

	const currentVisibilityMode = object.userData.visibility[VisibilityManager.getRenderMode()];
	let overrideMode = object.userData.visibility.overrideParent ? currentVisibilityMode : visibilityMode;

	if(!VisibilityManager.manuallyHiddenObjects.includes(object))
	{
		if((object as any).onSetVisibilityMode)
		{
			if(currentVisibilityMode === VisibilityModes.Hidden)
			{
				overrideMode = currentVisibilityMode;
			}
			else if(currentVisibilityMode === VisibilityModes.Ghosted && visibilityMode !== VisibilityModes.Hidden)
			{
				overrideMode = VisibilityModes.Ghosted;
			}
			else if(currentVisibilityMode === VisibilityModes.Custom)
			{
				customMaterial = VisibilityManager.getRenderMode() === RenderModes.Standalone ? object.userData.visibility.customStandaloneMaterial : object.userData.visibility.customDeviceMaterial;
				overrideMode = VisibilityModes.Custom;
			}

			(object as any).onSetVisibilityMode(overrideMode);
		}
		else
		{
			const currentMaterialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
			const editableMaterial = object instanceof THREE.Mesh && object.material && currentMaterialAsset && !(object instanceof THREE.Sprite) &&
			(!object.material.userData.type || object.material.userData.type !== 'modelRoot');
			const initialMaterial = (object as THREE.Mesh).material;

			switch (overrideMode)
			{
				case VisibilityModes.Hidden:
					if (editableMaterial)
					{
						object.material = CustomMaterials.getHiddenMaterial();
					}
					break;
				case VisibilityModes.Ghosted:
					if (currentVisibilityMode == VisibilityModes.Hidden)
					{
						overrideMode = VisibilityModes.Hidden;
						if (editableMaterial)
						{
							object.material = CustomMaterials.getHiddenMaterial();
						}
					}
					else if (editableMaterial)
					{
						object.material = CustomMaterials.getGhostMaterial();
					}
					break;
				case VisibilityModes.Custom:
					switch (currentVisibilityMode)
					{
						case VisibilityModes.Hidden:
							if (editableMaterial)
							{
								object.material = CustomMaterials.getHiddenMaterial();
							}
							overrideMode = VisibilityModes.Hidden;
							break;
						case VisibilityModes.Ghosted:
							if (editableMaterial)
							{
								object.material = CustomMaterials.getGhostMaterial();
							}
							overrideMode = VisibilityModes.Ghosted;
							break;
						case VisibilityModes.Custom:
						case VisibilityModes.Solid:
							const existingCustomMaterial = VisibilityManager.getRenderMode() === RenderModes.Standalone ? object.userData.visibility.customStandaloneMaterial : object.userData.visibility.customDeviceMaterial;
							customMaterial = existingCustomMaterial ?? customMaterial;
							if (editableMaterial)
							{
								const customMaterialAsset = MaterialAsset.getMaterialAssetByGuid(customMaterial);
								const materialAsset = customMaterialAsset ?? currentMaterialAsset;
								const materialTimelineInstance = MaterialAsset.getTimelineMaterialInstance(object);
								if (materialTimelineInstance)
								{
									MaterialAsset.updateMaterialTimelineInstance(object, materialAsset.internalMaterial!);
								}
								else
								{
									object.material = materialAsset.internalMaterial;
								}
							}
							break;
						default:
							assert(false, "unrecognized visibility mode: " + currentVisibilityMode);
							break;
					}
					break;
				case VisibilityModes.Solid:
					overrideMode = currentVisibilityMode;
					switch (currentVisibilityMode)
					{
						case VisibilityModes.Hidden:
							if (editableMaterial)
							{
								object.material = CustomMaterials.getHiddenMaterial();
							}
							break;
						case VisibilityModes.Ghosted:
							if (editableMaterial)
							{
								object.material = CustomMaterials.getGhostMaterial();
							}
							break;
						case VisibilityModes.Custom:
							customMaterial = VisibilityManager.getRenderMode() === RenderModes.Standalone ? object.userData.visibility.customStandaloneMaterial : object.userData.visibility.customDeviceMaterial;
							if (editableMaterial)
							{
								const customMaterialAsset = MaterialAsset.getMaterialAssetByGuid(customMaterial);
								const materialAsset = customMaterialAsset ?? currentMaterialAsset;
								const materialTimelineInstance = MaterialAsset.getTimelineMaterialInstance(object);
								if (materialTimelineInstance)
								{
									MaterialAsset.updateMaterialTimelineInstance(object, materialAsset.internalMaterial!);
								}
								else
								{
									object.material = materialAsset.internalMaterial;
								}
							}
							break;
						case VisibilityModes.Solid:
							if (editableMaterial)
							{
								const materialAsset = currentMaterialAsset;
								const materialTimelineInstance = MaterialAsset.getTimelineMaterialInstance(object);
								if (materialTimelineInstance)
								{
									MaterialAsset.updateMaterialTimelineInstance(object, materialAsset.internalMaterial!);
								}
								else
								{
									object.material = materialAsset.internalMaterial;
								}
							}
							break;
						default:
							assert(false, "unrecognized visibility mode: " + currentVisibilityMode);
							break;
					}
					break;
				default:
					assert(false, "unrecognized visibility mode: " + visibilityMode);
			}

			if(editableMaterial)
			{
				object.renderOrder = object.material.userData.type === MaterialTypes.DepthMask ? -2 : 0;
				if(object.material === initialMaterial && !forceUpdate && overrideMode !== VisibilityModes.Custom) return;
			}
		}
	}

	if(object.userData.fadeInFactor !== undefined) SceneObjectUtils.applyFadeInEffect(object);
	if(object.userData.overrideColorFactor !== undefined) SceneObjectUtils.applyColorChangeEffect(object);

	for (const child of object.children)
	{
		setVisibilityModeRecursive(child, overrideMode, customMaterial, forceUpdate);
	}
}
